import { Star } from "lucide-react";
import { testimonials } from "../../../data/contact";
import Reveal from "../../ui/Reveal";
import Section from "../../ui/Section";

const pathwayColors = {
  "Undergraduate Abroad": "bg-brand-gold-soft/60 text-brand-gold-strong",
  "Master's in India": "bg-emerald-50 text-emerald-700",
  "Bachelor's in India": "bg-purple-50 text-purple-700",
  "MBBS in India": "bg-blue-50 text-blue-700",
  "MBBS Abroad": "bg-orange-50 text-orange-700"
};

function AnimatedAvatar({ name, colorClass }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="relative h-14 w-14 shrink-0">
      {/* Outer pulse ring */}
      <span
        className={`absolute inset-0 rounded-full bg-gradient-to-br ${colorClass} opacity-30 animate-ping`}
        style={{ animationDuration: "3s" }}
      />
      {/* Inner ring */}
      <span
        className={`absolute inset-1 rounded-full bg-gradient-to-br ${colorClass} opacity-20`}
      />
      {/* Avatar circle */}
      <span
        className={`absolute inset-0 flex items-center justify-center rounded-full bg-gradient-to-br ${colorClass} shadow-md`}
      >
        <span className="text-base font-bold text-white">{initials}</span>
      </span>
    </div>
  );
}

function StarRating({ count }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-brand-gold-strong text-brand-gold-strong" />
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial, delay = 0 }) {
  const badgeClass =
    pathwayColors[testimonial.pathway] || "bg-brand-sky/50 text-brand-navy";

  return (
    <Reveal delay={delay}>
      <article className="group flex h-full flex-col rounded-2xl border border-brand-line/50 bg-white p-6 shadow-card transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-premium">
        {/* Stars + pathway badge */}
        <div className="flex items-center justify-between">
          <StarRating count={testimonial.rating} />
          <span
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${badgeClass}`}
          >
            {testimonial.pathway}
          </span>
        </div>

        {/* Quote */}
        <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-brand-charcoal/80">
          <span className="mr-0.5 text-2xl font-serif leading-none text-brand-gold-strong">&ldquo;</span>
          {testimonial.quote}
          <span className="ml-0.5 text-2xl font-serif leading-none text-brand-gold-strong">&rdquo;</span>
        </blockquote>

        {/* Divider */}
        <div className="my-5 h-px bg-brand-line/60 transition-all duration-500 group-hover:bg-brand-gold/30" />

        {/* Author */}
        <div className="flex items-center gap-3">
          <AnimatedAvatar
            name={testimonial.name}
            colorClass={testimonial.avatarColor}
          />
          <div>
            <p className="text-sm font-semibold text-brand-navy">
              {testimonial.name}
            </p>
            <p className="mt-0.5 text-xs text-brand-charcoal/60">
              {testimonial.city}
            </p>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

function TestimonialsSection() {
  return (
    <Section
      id="testimonials"
      tone="white"
      className="border-t border-brand-line/45 overflow-hidden"
    >
      {/* Section header */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold-strong">
            Student Stories
          </p>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-brand-navy sm:text-5xl">
            Families who found clarity with TheCareerSpace
          </h2>
          <p className="mt-4 text-base text-brand-charcoal/72">
            Real experiences from students and parents across South India — in their own words.
          </p>
        </div>

        {/* Live indicator */}
        <div className="flex items-center gap-2.5 shrink-0">
          <span className="h-2 w-2 rounded-full bg-brand-emerald animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal/60">
            {testimonials.length} Verified Reviews
          </span>
        </div>
      </div>

      {/* Testimonials grid */}
      <div className="mt-12 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {testimonials.slice(0, 6).map((t, i) => (
          <TestimonialCard key={t.id} testimonial={t} delay={i * 0.07} />
        ))}
      </div>

      {/* Extra row for 7th and 8th */}
      {testimonials.length > 6 && (
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          {testimonials.slice(6).map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} delay={(6 + i) * 0.07} />
          ))}
        </div>
      )}

      {/* Summary trust strip */}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-6 rounded-2xl border border-brand-gold/25 bg-brand-gold-soft/15 px-6 py-5">
        {[
          { value: "8+", label: "Student Stories" },
          { value: "5 ★", label: "Average Rating" },
          { value: "5", label: "Pathways Covered" },
          { value: "100%", label: "Real Families" }
        ].map((item) => (
          <div key={item.label} className="text-center">
            <p className="text-xl font-bold text-brand-navy">{item.value}</p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-charcoal/60">
              {item.label}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default TestimonialsSection;
